import React, { useEffect, useState } from 'react';
import NormalHeader from '../components/NormalHeader';
import NormalFooter from '../components/NormalFooter';
import { reservationApi } from '../../services/api';

interface Reservation {
  _id: string;
  reservationNumber: string;
  clinicName: string;
  prefecture: string;
  dateTime: string;
  lastName: string;
  firstName: string;
  phone: string;
  email: string;
  request?: string;
  createdAt: string;
}

const NormalReservationList: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setLoading(true);
        const data = await reservationApi.getAll();
        setReservations(data);
      } catch (err) {
        setError('予約情報の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };
    fetchReservations();
  }, []);

  const filtered = reservations.filter((item) => {
    if (!search.trim()) return true;
    const keyword = search.toLowerCase();
    return (
      item.reservationNumber.toLowerCase().includes(keyword) ||
      item.lastName.toLowerCase().includes(keyword) ||
      item.firstName.toLowerCase().includes(keyword) ||
      item.email.toLowerCase().includes(keyword) ||
      item.phone.includes(search)
    );
  });

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <NormalHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6">
        <div className="mb-6">
          <p className="text-sm font-semibold text-brand-primary">Reservation List</p>
          <h1 className="text-2xl font-bold text-gray-900">予約一覧</h1>
          <p className="text-sm text-gray-600">予約番号や名前で検索できます。</p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="予約番号、名前、メール、電話番号で検索"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary sm:w-72"
            />
            <span className="text-xs text-gray-500">{filtered.length} 件</span>
          </div>

          {loading ? (
            <p className="mt-4 text-sm text-gray-600">読み込み中...</p>
          ) : error ? (
            <p className="mt-4 text-sm text-red-600">{error}</p>
          ) : filtered.length === 0 ? (
            <p className="mt-4 text-sm text-gray-600">該当する予約がありません。</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">予約番号</th>
                    <th className="px-3 py-2 text-left">クリニック</th>
                    <th className="px-3 py-2 text-left">予約日時</th>
                    <th className="px-3 py-2 text-left">お名前</th>
                    <th className="px-3 py-2 text-left">連絡先</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((reservation) => (
                    <tr key={reservation._id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs font-semibold text-gray-900">
                        {reservation.reservationNumber}
                      </td>
                      <td className="px-3 py-2 text-gray-800">{reservation.clinicName}</td>
                      <td className="px-3 py-2 text-gray-800">{reservation.dateTime}</td>
                      <td className="px-3 py-2 text-gray-800">
                        {reservation.lastName} {reservation.firstName}
                      </td>
                      <td className="px-3 py-2 text-gray-800">{reservation.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <NormalFooter />
    </div>
  );
};

export default NormalReservationList;
